import { PurchaseLabelError } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import { useHandler } from "@redotech/react-util/hook";
import { useState } from "react";
import {
  PurchaseDropoffProps,
  usePurchaseDropoff,
} from "../../../hooks/use-purchase-dropoff";
import {
  PurchaseLabelProps,
  usePurchaseLabel,
} from "../../../hooks/use-purchase-label";
import { ConfirmationPageInfo, Page } from "../create-label-modal-types";

type SubmitArgs =
  | { method: "label"; props: PurchaseLabelProps }
  | { method: "dropoff"; props: PurchaseDropoffProps };

export function usePurchaseReturnShippingFlow({
  setPage,
  onShippingCreated,
}: {
  setPage: (page: Page) => void;
  onShippingCreated?: (info: ConfirmationPageInfo) => void;
}) {
  const purchaseLabelMut = usePurchaseLabel();
  const purchaseDropoffMut = usePurchaseDropoff();
  const [confirmationPageInfo, setConfirmationPageInfo] = useState<
    ConfirmationPageInfo | undefined
  >();

  const finish = (info: ConfirmationPageInfo) => {
    if (info.success && onShippingCreated) {
      onShippingCreated(info);
      return;
    }
    setConfirmationPageInfo(info);
    setPage(Page.CONFIRMATION);
  };

  const fail = () => {
    setConfirmationPageInfo({
      success: false,
      error: PurchaseLabelError.INTERNAL_ERROR,
    });
    setPage(Page.CONFIRMATION);
  };

  const submit = useHandler((args: SubmitArgs) => {
    if (args.method === "label") {
      purchaseLabelMut.mutate(args.props, {
        onSuccess: (data) => {
          if (data.success) {
            finish({
              success: true,
              kind: "label",
              labelUrl: data.labelUrl ?? "",
              qrCodeUrl: data.qrCodeUrl,
              trackingNumber: data.trackingNumber,
              charge: data.charge,
            });
          } else {
            setConfirmationPageInfo({
              success: false,
              error: data.error ?? PurchaseLabelError.INTERNAL_ERROR,
            });
            setPage(Page.CONFIRMATION);
          }
        },
        onError: fail,
      });
      return;
    }

    purchaseDropoffMut.mutate(args.props, {
      onSuccess: (data) => {
        if (data.success) {
          finish({
            success: true,
            kind: "dropoff",
            labelUrl: "",
            qrCodeUrl: data.qrCodeUrl,
            trackingNumber: data.trackingNumber,
            charge: data.charge,
          });
        } else {
          setConfirmationPageInfo({
            success: false,
            error: PurchaseLabelError.INTERNAL_ERROR,
          });
          setPage(Page.CONFIRMATION);
        }
      },
      onError: fail,
    });
  });

  return {
    confirmationPageInfo,
    submit,
    isPending: purchaseLabelMut.isPending || purchaseDropoffMut.isPending,
  };
}
